import { useCallback, useState } from 'react';
import {
  readUserPreferences,
  writeUserPreferences,
  type UserPreferences,
} from '../storage/userPreferences';

/**
 * Čte a zapisuje jednu položku z per-user úložiště voleb.
 *
 * Volající zná jen svoje pole ze schématu {@link UserPreferences}; kde a jak
 * se to ukládá řeší `storage/userPreferences`. Nová zapamatovaná volba se
 * proto přidává do schématu, ne jako další klíč v `localStorage`.
 *
 * @param userId ID přihlášeného uživatele; dokud není známé (načítá se
 * `/user`), jede se na výchozích hodnotách a nic se neukládá.
 */
export function useUserPreference<K extends keyof UserPreferences>(
  userId: number | undefined,
  key: K,
): [UserPreferences[K], (value: Partial<UserPreferences[K]>) => void] {
  // `userId` dorazí až s odpovědí /user. Uložená volba se proto dopočítává
  // při renderu (React „adjusting state when props change"), ne v efektu —
  // ten by na každou změnu uživatele vyvolal druhý render.
  const [state, setState] = useState(() => ({
    userId,
    preferences: readUserPreferences(userId),
  }));

  if (state.userId !== userId) {
    setState({ userId, preferences: readUserPreferences(userId) });
  }

  const update = useCallback(
    (partial: Partial<UserPreferences[K]>) => {
      setState((current) => {
        const preferences: UserPreferences = {
          ...current.preferences,
          [key]: { ...current.preferences[key], ...partial },
        };
        if (current.userId !== undefined) {
          writeUserPreferences(current.userId, preferences);
        }
        return { ...current, preferences };
      });
    },
    [key],
  );

  return [state.preferences[key], update];
}
